import { useState } from 'react'

import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { DataClassAddressTextExtractDrawer } from './DataClass.AddressTextExtractDrawer'
import { Button } from '@/Atomic/Molecules/Button'
import InfoBox from '@/Atomic/Molecules/InfoBox'
import { CustomField } from '@/Atomic/Organisms/CustomField'
import DropdownMenu from '@/Atomic/Organisms/DropdownMenu'
import {
  Drawer,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
} from '@/Atomic/Templates/Drawer'
import { Form, FormBody, FormFields, FormGroup } from '@/Atomic/Templates/Form'
import type { AddressFields } from '@/Components/Address/addressTextExtract'
import { errorToast } from '@/Components/Toast'
import { Address } from '@/Data/Address/AddressDataClass'
import { AddressContactRelation } from '@/Data/AddressContactRelation/AddressContactRelationDataClass'
import { useDataClassFormHandler } from '@/utils/CRUD/hooks/useDataClassFormHandler'
import { getDataClassFormMap } from '@/utils/CRUD/read/dataClass'

interface DataClassAddressDrawerProps {
  contactId: string
  contactName: string | null
  isOpen: boolean
  close: () => void
  onCreated?: (addressId: string) => void
}

export const DataClassAddressDrawer: React.FC<DataClassAddressDrawerProps> = ({
  contactId,
  contactName,
  isOpen,
  close,
  onCreated,
}) => {
  const { t } = useTranslation()
  const [isAiDrawerOpen, setIsAiDrawerOpen] = useState(false)

  const { methods } = useDataClassFormHandler(Address, {
    prefill: { enabled: isOpen },
  })
  const { getValues, watch, reset, handleSubmit, setValue } = methods
  const groups = getDataClassFormMap(Address, getValues, watch)

  const onDrawerClose = () => {
    setIsAiDrawerOpen(false)
    reset()
    close()
  }

  const handleCreate = handleSubmit(async (data) => {
    try {
      const address = (await Address.create(data)).id()

      await AddressContactRelation.create({ contact: contactId, address })
      reset()
      close()
      onCreated?.(address)
    } catch (error) {
      errorToast(
        error instanceof Error
          ? error
          : new Error(t('errors.unknownError.headline')),
      )
    }
  })

  const handleExtracted = (data: AddressFields) => {
    for (const key in data) {
      const val = data[key as keyof AddressFields]
      if (val !== null) {
        setValue(key, val, { shouldDirty: true, shouldValidate: true })
      }
    }
    setIsAiDrawerOpen(false)
  }

  return (
    <>
      <Drawer isOpen={isOpen} close={onDrawerClose} direction="right">
        <DrawerHeader>
          <div className="header item">
            <Button
              variant="icon"
              icon="cross"
              tooltip={t('tooltip.close')}
              onClick={onDrawerClose}
            />
            {t('components.address.add')}
          </div>
          <div className="header item">
            <DropdownMenu
              buttonClassName="borderless button"
              menuItems={[
                {
                  icon: 'scan',
                  label: t('components.address.textExtract.menuItem'),
                  onClick: () => setIsAiDrawerOpen(true),
                },
              ]}
            />
          </div>
        </DrawerHeader>
        <DrawerBody>
          <FormProvider {...methods}>
            <Form onSubmit={handleCreate}>
              <FormBody>
                {contactName && (
                  <InfoBox
                    iconPosition="side"
                    text={t('components.address.linkedTo', { contactName })}
                  />
                )}
                <FormGroup>
                  <FormFields>
                    {groups.flatMap((group) =>
                      group.fields.map((field) => (
                        <CustomField
                          key={field.fieldKey}
                          fieldMap={field}
                          isEditing={true}
                          className="form-field"
                        />
                      )),
                    )}
                  </FormFields>
                </FormGroup>
              </FormBody>
            </Form>
          </FormProvider>
        </DrawerBody>
        <DrawerFooter align="right">
          <Button variant="outlined" onClick={onDrawerClose} size="lg">
            {t('common.cancel')}
          </Button>
          <Button icon="disk" onClick={handleCreate} size="lg">
            {t('common.save')}
          </Button>
        </DrawerFooter>
      </Drawer>
      <DataClassAddressTextExtractDrawer
        isOpen={isAiDrawerOpen}
        close={() => setIsAiDrawerOpen(false)}
        onExtracted={handleExtracted}
      />
    </>
  )
}
